import React from 'react';
import PropTypes from 'prop-types';

import {
  ROLES,
  MODERATOR,
  BILLER,
  JORNALIST,
  EDITOR,
  CORRECTOR,
  SUPERVISOR,
} from 'utils/roles';

import Employees from 'api/resources/employees';
import { processErrorByAlert } from 'utils/getErrors';
import { Deserializer } from 'jsonapi-serializer';
import Alert from 'react-s-alert';

import DeleteIcon from 'icons/Delete';
import { ReactComponent as Chevron } from 'icons/Chevron.svg';

import RolesForm from 'admin-moderator/components/RolesForm';
import Avatar from 'components/Avatar';
import Popup from 'components/Popup';
import ConfirmModal from 'components/ConfirmModal';

import Button from 'styled/Buttons/PrimaryButton';
import List from 'styled/SimpleList';
import SecondaryButton from 'styled/Buttons/SecondaryButton';
import Footer from 'components/ConfirmModal/Footer';
import Text from 'components/ConfirmModal/Text';
import Content from 'components/ConfirmModal/Content';
import Title from 'components/ConfirmModal/Title';

import ItemCard from 'styled/ItemCard/Card';
import Name from 'styled/ItemCard/Name';
import Info from 'styled/ItemCard/Info';
import CardFooter from 'styled/ItemCard/CardFooter';

import Roles from './Roles';
import Role from './Role';
import DeleteButton from './DeleteButton';
import RolesFormWrapper from './RolesFormWrapper';
import Rotate from './Rotate';
import CardContent from './CardContent';

const FORMATED_ROLES = ROLES.reduce((acc, role) => ({
  ...acc,
  [role.value]: role.label,
}), {});

export class Employee extends React.PureComponent {
  button = React.createRef();

  state = {
    isOpen: false,
    confirmIsOpen: false,
  };

  get popupPosition() {
    const DEFAULT_VALUE = 10;
    if (!this.button.current) return `${DEFAULT_VALUE}px`;

    const CLIENT_HEIGHT = document.documentElement.clientHeight;
    const DROPDOWN_HEIGHT = 210;
    const OFFSET_TOP = this.button.current.offsetTop + this.button.current.offsetHeight;
    const OFFSET_BOTTOM = CLIENT_HEIGHT - (OFFSET_TOP - window.scrollY);

    if ((OFFSET_BOTTOM - DROPDOWN_HEIGHT) < 0) {
        return (`-${(
          DROPDOWN_HEIGHT
          + this.button.current.offsetHeight
          + DEFAULT_VALUE
        )}px`
      );
    }
    return `${DEFAULT_VALUE}px`;
  }

  handleClickOnBackdrop = () => {
    this.setState({ isOpen: false });
  }

  handlePopupOpen = () => {
    this.setState({ isOpen: true });
  }

  handleOpenModalClick = () => {
    this.setState({ confirmIsOpen: true });
  }

  handleCloseModalClick = () => {
    this.setState({ confirmIsOpen: false });
  }

  handleDeleteClick = async () => {
    this.setState({ loading: true });

    try {
      const response = await Employees.patch({
        employeeId: this.props.id,
        body: {
          data: {
            type: 'employee',
            attributes: {
              is_disabled: true,
            },
          },
        },
      });
      const employee = await new Deserializer({ keyForAttribute: 'camelCase' }).deserialize(response);

      Alert.success('Сотрудник успешно удален');
      this.props.onDelete(employee);
    } catch (err) {
      processErrorByAlert(err);
      this.setState({ loading: false });
    }
  }

  handleRolesChange = async (roles) => {
    this.setState({ loading: true });

    try {
      const response = await Employees.patch({
        employeeId: this.props.id,
        body: {
          data: {
            type: 'employee',
            attributes: {
              roles,
            },
          },
        },
        include: 'avatar',
      });

      this.setState({ loading: false });
      Alert.success('Список ролей обновлён');

      const employee = await new Deserializer({ keyForAttribute: 'camelCase' }).deserialize(response);

      this.props.onRolesChange(employee);
    } catch (err) {
      processErrorByAlert(err);
      this.setState({ loading: false });
    }
  }

  render() {
    return (
      <ItemCard>
        <ConfirmModal onClose={this.handleCloseModalClick} isOpen={this.state.confirmIsOpen}>
          <Content>
            <Title>Удаление</Title>

            <Text>Вы точно хотите удалить сотрудника {this.props.name}?</Text>
          </Content>

          <Footer>
            <Button
              type="button"
              onClick={this.handleDeleteClick}
              autoFocus
              style={{ marginRight: '10px' }}
              disabled={this.state.loading}
            >
              Удалить
            </Button>

            <SecondaryButton
              type="button"
              onClick={this.handleCloseModalClick}
            >
              Отмена
            </SecondaryButton>
          </Footer>
        </ConfirmModal>

        <CardContent>
          <DeleteButton type="button" onClick={this.handleOpenModalClick}>
            <DeleteIcon />
          </DeleteButton>

          <Avatar
            size="88px"
            name={this.props.name}
            fontSize="24px"
            url={this.props.avatar}
          />
        </CardContent>

        <CardFooter>
          <Info>{this.props.email.replace('@gmail.com', '')}</Info>

          {this.props.name &&
            <Name>{this.props.name.trim()}</Name>
          }

          <List>
            <Roles
              type="button"
              onClick={this.handlePopupOpen}
              style={{ display: 'flex' }}
              ref={this.button}
            >
              {this.props.roles.map((value, index) => (FORMATED_ROLES[value] && (
                <Role key={value}>
                  {FORMATED_ROLES[value]}

                  {index === this.props.roles.length - 1 && (
                    <Rotate style={{ transform: this.state.isOpen ? 'rotate(180deg)' : '' }}>
                      <Chevron />
                    </Rotate>
                  )}
                </Role>
              )))}
            </Roles>

            <RolesFormWrapper style={this.state.isOpen ? { top: this.popupPosition } : {}}>
              <Popup
                isVisible={this.state.isOpen}
                onClickOnBackdrop={this.handleClickOnBackdrop}
                isControlled
              >
                <RolesForm
                  onRolesChange={this.handleRolesChange}
                  roles={this.props.roles}
                  loading={this.state.loading}
                  hideSupervisor
                />
              </Popup>
            </RolesFormWrapper>
          </List>
        </CardFooter>
      </ItemCard>
    );
  }
}

Employee.propTypes = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string,
  email: PropTypes.string,
  avatar: PropTypes.string,
  roles: PropTypes.arrayOf(
    PropTypes.oneOf([EDITOR, SUPERVISOR, MODERATOR, CORRECTOR, BILLER, JORNALIST])
  ),
  onDelete: PropTypes.func,
  onRolesChange: PropTypes.func,
};

Employee.defaultProps = {
  name: '',
  email: '',
  avatar: '',
  roles: [],
  onDelete: () => {},
  onRolesChange: () => {},
};

export default Employee;
